import { SimpleGrid, Button, Dialog, Stack, Field, Input, Select, Portal, FileUpload } from "@chakra-ui/react";
import { LuPlus, LuDatabase, LuLink, LuFilePlus2, LuEraser, LuUpload, LuHardDriveDownload } from "react-icons/lu";
import Type from "../../../../../utilies/Types";
import { Tooltip } from "../../../../ui/tooltip";
import { getGeojsonType, readAsArrayBuffer, readAsText, showToast, SUFFIX_TO_TYPE } from "../../../../../utilies/Utility";
import { loadGeojsonImageType } from "../../../../../utilies/loader/LoadCamera";
import { loadGeojsonTypeAsync } from "../../../../../utilies/loader/LoadGeojson";
import { loadGlbTypeAsync } from "../../../../../utilies/loader/LoadGLB";
import { loadCSVTypeAsync } from "../../../../../utilies/loader/LoadCSV";
import { loadCzmlTypeAsync } from "../../../../../utilies/loader/loadCzml";
import JSZip from "jszip";
import { loadOsmTypeAsync } from "../../../../../utilies/loader/LoadOSM";
import { useAppStore } from "../../../../../context/store";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
import { loadKmlTypeAsync } from "../../../../../utilies/loader/LoadKML";
import { useState } from "react";

const LayerImportRow = ({ uploadKey, setUploadKey, selectedImportType, setSelectedImportType, importFromUrl, createNewFile, cleanMap, exportAllLayers }) => {
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const defaultOsmStyle = useAppStore((s) => s.defaultOsmStyle);
  const importTypeOptions = useAppStore((s) => s.importTypeOptions);
  const userType = useAppStore((s) => s.userType);
  const importUrls = useAppStore((s) => s.importUrls);

  const viewerRef = useAppStore.getState().viewerRef;
  const originalCameraControls = useAppStore.getState().originalCameraControls;

  const setImageGeojsonMode = useAppStore.getState().setImageGeojsonMode;
  const setImageGeojson = useAppStore.getState().setImageGeojson;
  const setImageGeojsonSelected = useAppStore.getState().setImageGeojsonSelected;
  const setImageDataSource = useAppStore.getState().setImageDataSource;
  const { setMapObjects } = useMapObjects();
  const setImageGeojsonCounter = useAppStore.getState().setImageGeojsonCounter;
  const setActiveLayer = useAppStore.getState().setActiveLayer;
  const setLayersApprovalLoadLayerDialog = useAppStore.getState().setLayersApprovalLoadLayerDialog;
  const setShowImportFromSQLDialog = useAppStore.getState().setShowImportFromSQLDialog;
  const setShowLoadLayersDialog = useAppStore.getState().setShowLoadLayersDialog;
  const loadingDataForLayers = useAppStore((s) => s.loadingDataForLayers);

  const [url, setUrl] = useState("https://");

  // helper: detect type id from normalized URL
  function detectImportTypeFromUrl(normalizedUrl, importTypeOptions) {
    // strip query and hash
    const clean = String(normalizedUrl || "")
      .split(/[?#]/)[0]
      .toLowerCase();

    // exact suffix mapping
    for (const suffix of Object.keys(SUFFIX_TO_TYPE)) {
      if (clean.endsWith(suffix)) return SUFFIX_TO_TYPE[suffix];
    }

    // try to match by keyword against option id or name
    for (const item of importTypeOptions?.items || []) {
      const id = String(item.id || "").toLowerCase();
      const name = String(item.name || "").toLowerCase();
      for (const key of Object.values(SUFFIX_TO_TYPE)) {
        if (id.includes(key) || name.includes(key)) return item.id;
      }
    }

    // no match
    return null;
  }
  // handler to normalize user input and set url
  function handleUrlChange(e) {
    const raw = (e.target.value || "").trim();
    if (!raw) {
      setUrl("");
      return;
    }

    // keep full http(s) urls
    if (/^https?:\/\//i.test(raw)) {
      setUrl(raw);
      // detect type from the full url
      const t = detectImportTypeFromUrl(raw, importTypeOptions);
      if (t) setSelectedImportType(t);
      return;
    }

    // remove leading slashes
    const noLeading = raw.replace(/^\/+/, "");

    // prepare normalized base
    const BASE = "https://matrix.gatik.ai/static/layers/";
    const BASE_NORM = BASE.replace(/\/+$/, "") + "/"; // ensure single trailing slash
    const BASE_NO_PROTO = BASE_NORM.replace(/^https?:\/\//i, "");

    // if pasted domain/path without protocol
    let normalized;
    if (noLeading.toLowerCase().startsWith(BASE_NO_PROTO.toLowerCase())) {
      const rest = noLeading.slice(BASE_NO_PROTO.length).replace(/^\/+/, "");
      normalized = BASE_NORM + rest;
    } else {
      normalized = BASE_NORM + noLeading;
    }

    setUrl(normalized);

    // detect and set select based on normalized url
    const detected = detectImportTypeFromUrl(normalized, importTypeOptions);
    if (detected) setSelectedImportType(detected);
  }

  const onSelectFile = async (evt) => {
    const list = Array.from(evt?.files?.files ?? evt?.files ?? []);

    for (const file of list) {
      const sizeInBytes = file.size || new TextEncoder().encode(file.content).length;
      const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);

      try {
        const name = file.name.toLowerCase();

        if (name.endsWith(".geojson")) {
          const text = await readAsText(file);
          const data = JSON.parse(text);

          switch (getGeojsonType(data)) {
            case Type.ASSET.GEOJSON_IMAGE:
              loadGeojsonImageType({
                viewerRef,
                setImageGeojson,
                setImageGeojsonSelected,
                setImageGeojsonMode,
                setImageDataSource,
                setMapObjects,
                setImageGeojsonCounter,
                geojsonData: data,
                fileName: file.name,
              });

              break;
            case Type.ASSET.GEOJSON:
              if (parseFloat(sizeInMB) >= 10) {
                setLayersApprovalLoadLayerDialog({
                  originalCameraControls,
                  geojsonData: data,
                  fileName: file.name,
                  viewerRef,
                  setMapObjects,
                  defaultGeojsonStyle,
                  basicGeojsonStyle,
                  defaultOsmStyle,
                });
              } else {
                loadGeojsonTypeAsync({
                  originalCameraControls,
                  geojsonData: data,
                  fileName: file.name,
                  viewerRef,
                  setMapObjects,
                  defaultGeojsonStyle,
                  basicGeojsonStyle,
                  defaultOsmStyle,
                });
              }

              break;
          }
        } else if (name.endsWith(".glb")) {
          const buf = await readAsArrayBuffer(file);
          await loadGlbTypeAsync({
            arrayBuffer: buf,
            fileName: file.name,
            viewerRef,
            setMapObjects,
            positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
            orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
            scale: 1.0,
          });
        } else if (name.endsWith(".zip")) {
          const buf = await readAsArrayBuffer(file);
          const zip = await JSZip.loadAsync(buf);
          for (const relPath of Object.keys(zip.files)) {
            const zf = zip.files[relPath];
            if (zf.dir) continue;

            try {
              const p = relPath.toLowerCase();

              if (p.endsWith(".geojson")) {
                const fileText = await zf.async("string");
                await loadGeojsonTypeAsync({
                  originalCameraControls,
                  geojsonData: JSON.parse(fileText),
                  fileName: relPath,
                  viewerRef,
                  setMapObjects,
                  defaultGeojsonStyle,
                  basicGeojsonStyle,
                  defaultOsmStyle,
                });
              } else if (p.endsWith(".glb")) {
                const fileBuf = await zf.async("arraybuffer");
                await loadGlbTypeAsync({
                  arrayBuffer: fileBuf,
                  fileName: relPath,
                  viewerRef,
                  setMapObjects,
                  positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
                  orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
                  scale: 1.0,
                });
              } else if (p.endsWith(".kml")) {
                const kmlText = await zf.async("string");
                await loadKmlTypeAsync({
                  kmlText,
                  fileName: relPath,
                  viewerRef,
                  setMapObjects,
                  clampToGround: true,
                });
              } else if (p.endsWith(".kmz")) {
                const kmzBuf = await zf.async("arraybuffer");
                await loadKmlTypeAsync({
                  fileOrBlob: new Blob([kmzBuf], { type: "application/vnd.google-earth.kmz" }),
                  fileName: relPath,
                  viewerRef,
                  setMapObjects,
                  clampToGround: true,
                });
              }
            } catch (_) {}
          }
        } else if (name.endsWith(".osm")) {
          const buf = await readAsArrayBuffer(file);
          await loadOsmTypeAsync({
            arrayBuffer: buf,
            fileName: file.name,
            viewerRef,
            setMapObjects,
            setActiveLayer,
            defaultOsmStyle,
            defaultGeojsonStyle,
            basicGeojsonStyle,
            loadingDataForLayers,
          });
        } else if (name.endsWith(".csv")) {
          const text = await readAsText(file);
          await loadCSVTypeAsync({
            text,
            fileName: file.name,
            viewerRef,
            setMapObjects,
            defaultGeojsonStyle,
          });
        } else if (name.endsWith(".czml")) {
          const text = await readAsText(file); // read file as text
          const czmlData = JSON.parse(text); // parse to JS object/array

          await loadCzmlTypeAsync({
            czmlData,
            fileName: file.name,
            viewerRef,
            setMapObjects,
          });
        } else if (name.endsWith(".kml") || name.endsWith(".kmz")) {
          await loadKmlTypeAsync({
            fileOrBlob: file, // File is a Blob
            fileName: file.name,
            viewerRef,
            setMapObjects,
            clampToGround: true,
          });
        }
      } catch (err) {
        showToast(err.message, Type.NOTIFICATION.ERROR);
        console.log(err);
      }
    }

    setUploadKey((prev) => prev + 1);
  };

  return (
    <SimpleGrid columns={7} gap={1} my={2} mb={0} width="full">
      <FileUpload.Root key={uploadKey} onFileAccept={onSelectFile} maxFiles={100} accept={[".json", ".geojson", ".glb", ".zip", ".osm", ".csv", ".czml", ".kml", ".kmz"]}>
        <FileUpload.HiddenInput />
        <FileUpload.Trigger asChild width="full">
          <Button variant="outline" size="2xl" p={0} height="65px">
            <Tooltip content="Import Layers From Local">
              <LuPlus size={20} />
            </Tooltip>
          </Button>
        </FileUpload.Trigger>
      </FileUpload.Root>

      <Button
        variant="outline"
        size="2xl"
        p={0}
        height="65px"
        onClick={() => {
          setShowImportFromSQLDialog(true);
        }}
      >
        <Tooltip content="Load From PostgreSQL">
          <LuDatabase size={20} />
        </Tooltip>
      </Button>

      <Dialog.Root closeOnInteractOutside={false}>
        <Dialog.Trigger asChild>
          <Button variant="outline" size="2xl" p={0} height="65px">
            <Tooltip content="Import Layer From URL">
              <LuLink size={20} />
            </Tooltip>
          </Button>
        </Dialog.Trigger>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Import From Url</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body pb="4">
                <Stack gap="4">
                  <Field.Root>
                    <Field.Label>URL</Field.Label>
                    <Input list="url-suggestions" placeholder="Enter Url Here . " value={url} onChange={handleUrlChange} />
                    <datalist id="url-suggestions">
                      {importUrls
                        .filter((item) => item.type === selectedImportType)
                        .map((item, i) => (
                          <option value={item.url} key={i} />
                        ))}
                    </datalist>
                  </Field.Root>
                </Stack>
                <Select.Root
                  collection={importTypeOptions}
                  size="sm"
                  width="full"
                  value={[selectedImportType]}
                  positioning={{ sameWidth: true }}
                  onValueChange={(value) => {
                    setSelectedImportType(value.value[0]);
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Label mt={2}>Select Type</Select.Label>
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="Select Type" />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {importTypeOptions.items.map((item) => (
                        <Select.Item item={item} key={item.id} justifyContent="flex-start">
                          {item.name}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </Dialog.ActionTrigger>
                <Dialog.ActionTrigger asChild>
                  <Button
                    onClick={() => {
                      importFromUrl(url);
                    }}
                  >
                    Load
                  </Button>
                </Dialog.ActionTrigger>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN)|| userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.SUPER_ADMIN)) && (
        <Button
          variant="outline"
          size="2xl"
          p={0}
          height="65px"
          onClick={() => {
            createNewFile();
          }}
        >
          <Tooltip content="Create New Layer">
            <LuFilePlus2 size={20} />
          </Tooltip>
        </Button>
      )}

      <Button variant="outline" size="2xl" p={0} height="65px" onClick={cleanMap}>
        <Tooltip content="Remove All Layers">
          <LuEraser size={20} />
        </Tooltip>
      </Button>

      <Button variant="outline" size="2xl" p={0} height="65px" onClick={exportAllLayers}>
        <Tooltip content="Export All Layers">
          <LuUpload size={20} />
        </Tooltip>
      </Button>

      <Button
        variant="outline"
        size="2xl"
        p={0}
        height="65px"
        onClick={() => {
          setShowLoadLayersDialog(true);
        }}
      >
        <Tooltip content="Load Layers From Server">
          <LuHardDriveDownload size={20} />
        </Tooltip>
      </Button>
    </SimpleGrid>
  );
};

export default LayerImportRow;
